// React Query hooks pros endpoints do ms-meta-reports.
// `useDashboardStore` já controla os filtros — esses hooks pegam o filterKey
// como query key e reagem automaticamente quando o usuário muda o filtro.

import { useQuery } from "@tanstack/react-query";
import { reportsApi, type ClickScope, type SortBy } from "./reportsApi";
import { filterKey } from "../lib/filters";
import { useDashboardStore } from "../store/dashboardStore";
import type { Filters } from "../types/reports";

// Helper pra extrair filters do store e usar como dep da query
function useFilters(): { filters: Filters; key: string } {
    const filters = useDashboardStore((s) => s.filters);
    return { filters, key: filterKey(filters) };
}

// Boolean cacheado: o user logado pode ver o dashboard?
// Usado pela sidebar pra esconder o item e pela rota pra redirecionar.
// Stale infinito intencionalmente — só recarrega num F5; permissão de
// colaborador não muda dinamicamente durante a sessão.
export function useDashboardAccess() {
    return useQuery({
        queryKey: ["reports", "me", "dashboard-access"],
        queryFn: () => reportsApi.dashboardAccess(),
        staleTime: Infinity,
        retry: false,
    });
}

export function useOverview() {
    const { filters, key } = useFilters();
    return useQuery({
        queryKey: ["reports", "overview", key],
        queryFn: () => reportsApi.overview(filters),
        placeholderData: (prev) => prev, // mantém data anterior durante refetch (evita flash)
    });
}

export function useFlowsList() {
    const { filters, key } = useFilters();
    return useQuery({
        queryKey: ["reports", "flows", key],
        queryFn: () => reportsApi.flows(filters),
        placeholderData: (prev) => prev,
    });
}

export function useFlowReport(flowId: string | undefined) {
    const { filters, key } = useFilters();
    return useQuery({
        queryKey: ["reports", "flow", flowId, key],
        queryFn: () => reportsApi.flow(flowId!, filters),
        enabled: !!flowId,
        placeholderData: (prev) => prev,
    });
}

export function useNodeDetail(flowId: string | undefined, nodeId: string | undefined) {
    const { filters, key } = useFilters();
    return useQuery({
        queryKey: ["reports", "node", flowId, nodeId, key],
        queryFn: () => reportsApi.node(flowId!, nodeId!, filters),
        enabled: !!flowId && !!nodeId,
        placeholderData: (prev) => prev,
    });
}

export function useStuckLeads(
    flowId: string | undefined,
    nodeId: string | undefined,
    opts: { limit?: number; offset?: number; filter?: string }
) {
    return useQuery({
        queryKey: ["reports", "stuckLeads", flowId, nodeId, opts.limit, opts.offset, opts.filter],
        queryFn: () => reportsApi.stuckLeads(flowId!, nodeId!, opts),
        enabled: !!flowId && !!nodeId,
        placeholderData: (prev) => prev,
    });
}

export function useBroadcastsList(
    page: number,
    pageSize = 24,
    groupBy: "none" | "template" = "none"
) {
    const { filters, key } = useFilters();
    return useQuery({
        queryKey: ["reports", "broadcasts", page, pageSize, groupBy, key],
        queryFn: () => reportsApi.broadcasts(filters, page, pageSize, groupBy),
        placeholderData: (prev) => prev,
    });
}

export function useBroadcastReport(bcId: string | undefined) {
    const { filters, key } = useFilters();
    return useQuery({
        queryKey: ["reports", "broadcast", bcId, key],
        queryFn: () => reportsApi.broadcast(bcId!, filters),
        enabled: !!bcId,
        placeholderData: (prev) => prev,
    });
}

export function useGroupedBroadcastReport(contentHash: string | undefined) {
    const { filters, key } = useFilters();
    return useQuery({
        queryKey: ["reports", "broadcastGrouped", contentHash, key],
        queryFn: () => reportsApi.broadcastGrouped(contentHash!, filters),
        enabled: !!contentHash,
        placeholderData: (prev) => prev,
        retry: false, // grouped pode dar 404 legítimo — caller resolve fallback
    });
}

export function useEsteiraList() {
    const { filters, key } = useFilters();
    return useQuery({
        queryKey: ["reports", "esteira", key],
        queryFn: () => reportsApi.esteira(filters),
        placeholderData: (prev) => prev,
    });
}

export function useEsteiraGroup(esId: string | undefined, sortBy: SortBy = "revenue") {
    const { filters, key } = useFilters();
    return useQuery({
        queryKey: ["reports", "esteiraGroup", esId, sortBy, key],
        queryFn: () => reportsApi.esteiraGroup(esId!, filters, sortBy),
        enabled: !!esId,
        placeholderData: (prev) => prev,
    });
}

export function useClicks(scope: ClickScope = { type: "all" }) {
    const { filters, key } = useFilters();
    return useQuery({
        queryKey: ["reports", "clicks", scope.type, scope.id, key],
        queryFn: () => reportsApi.clicks(filters, scope),
        placeholderData: (prev) => prev,
    });
}

export function useCountries() {
    const { filters, key } = useFilters();
    return useQuery({
        queryKey: ["reports", "countries", key],
        queryFn: () => reportsApi.countries(filters),
        placeholderData: (prev) => prev,
    });
}

export function useFilterOptions() {
    return useQuery({
        queryKey: ["reports", "filterOptions"],
        queryFn: () => reportsApi.filterOptions(),
        staleTime: 1000 * 60 * 10, // categorias/projetos mudam raramente
    });
}
